import {
    ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges,
    OnDestroy, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { isNil } from 'lodash';

import { OnlineBriefingTemplateEnum } from '../../enums/online-briefing-template.enum';
import { OnlineBriefingTemplateConfigModel } from '../../models/online-briefing-option.model';
import { deliverySchedulingTypesEnum } from '../../../../Common/Forms/enums/delivery-scheduling-types.enum';
import { MonitoringSeriesTypeEnum } from '../../../Shared/enums/monitoring-series-type.enum';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { ConfirmDialogModel } from '../../../../Common/Dialogs/models/confirm-dialog.model';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { MentionSearchSettingService } from '../../../../Common/MonitorShared/services/mention-search-setting.service';
import { MonitoringBrandManagementService } from '../../../Shared/services/monitoring-brand-management.service';
import { ConfirmDialogComponent } from '../../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { MonitoringSourcePickerFieldOptions } from '../../../Shared/models/monitoring-source-picker-field.model';
import { MonitoringBrandCardComponent, MonthlyConfigs, QuarterlyConfigs } from '../../../Shared/components/MonitoringBrandCard/monitoring-brand-card.component';
import { MonitoringBrand } from '../../../Shared/models/monitoring-brand.model';

@Component({
    selector: 'agpr-briefing-canvas-template-setup',
    templateUrl: './briefing-canvas-template-setup.component.html',
    styleUrls: ['./briefing-canvas-template-setup.component.scss'],
    standalone: false,
    providers: [
        MentionSearchSettingService,
        MonitoringBrandManagementService
    ]
})
export class BriefingCanvasTemplateSetupComponent implements OnChanges, OnDestroy {

    readonly deliveryTypes = deliverySchedulingTypesEnum;
    readonly onlineBriefingTemplateEnum = OnlineBriefingTemplateEnum;
    readonly buttonTypesEnum = ButtonTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;

    // The analysis focus is reused as a section title that is persisted to the BriefingCuratedMentions.title
    // column (VARCHAR(255)), after the quarterly template decorates it with prefixes/suffixes such as
    // "Most Impactful Mentions (High Impressions, High Brand Prominence)" (~66 chars). Cap the focus below
    // 255 to leave headroom for that decoration so the generated title never overflows the column.
    readonly analysisFocusMaxLength = 180;

    @Input() id!: string;
    @Input() canvasPreview!: OnlineBriefingTemplateConfigModel;

    @Output() changes = new EventEmitter<void>();

    @ViewChildren('competitorCard') competitorCards!: QueryList<ElementRef<HTMLElement>>;
    @ViewChildren(MonitoringBrandCardComponent) allBrandCards: QueryList<MonitoringBrandCardComponent> = new QueryList();
    @ViewChildren(MonitoringBrandCardComponent, {read: ElementRef}) allBrandCardRefs: QueryList<ElementRef> = new QueryList();

    @ViewChild('brandCardTemp') brandCardTemp: MonitoringBrandCardComponent;
    @ViewChild('brandCardDataSourceRef') brandCardDataSourceRef: ElementRef | undefined;

    private readonly destroy$ = new Subject<void>();

    brandCardDataSourceConfigs: MonitoringSourcePickerFieldOptions;
    competitorCardDataSourceConfigs: MonitoringSourcePickerFieldOptions;
    quarterlyConfigs: QuarterlyConfigs;
    brandCard: MonitoringBrand;
    duplicateBrandIds: Record<number, boolean> = {};
    monthlyConfigs: MonthlyConfigs;

    constructor(
        private coreDialogService: CoreDialogService,
        private localizationService: LocalizationService,
        private mentionSearchSettingService: MentionSearchSettingService,
        private monitoringBrandManagementService: MonitoringBrandManagementService,
        public cdr: ChangeDetectorRef,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.hasOwnProperty('canvasPreview')) {
            this.brandCard ??= new MonitoringBrand();
            this.brandCard.brand = this.canvasPreview.brand;
            this.brandCard.name = this.canvasPreview.name;
            this.brandCard.logo = this.canvasPreview.logo;
            this.brandCard.color = this.canvasPreview.accentColor;
            this.brandCard.dataSource = this.canvasPreview.dataSource;
            this.updateDataSourceConfigs();
            this.updateQuartlyConfigs();
            this.updateMonthlyConfigs();
            this.updateDuplicateBrandIds();
        }
    }

    private updateDuplicateBrandIds(): void {
        this.duplicateBrandIds = this.canvasPreview.template === OnlineBriefingTemplateEnum.ONLINE_BRIEFING_TEMPLATE_COMPETITOR
            ? this.monitoringBrandManagementService.getDuplicateBrandIds(this.brandCard, this.canvasPreview.competitors)
            : {};
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    typeChanged(type: deliverySchedulingTypesEnum): void {
        this.canvasPreview.deliverySchedulingType = type;
        this.mentionSearchSettingService.updateSelectType(
            type === deliverySchedulingTypesEnum.recurring ? MonitoringSeriesTypeEnum.topic : MonitoringSeriesTypeEnum.folder
        );
        this.updateQuartlyConfigs();
        this.updateMonthlyConfigs();
        this.changes.emit();
    }

    updateDataSourceConfigs() {
        this.brandCardDataSourceConfigs = {
            showResultsFeedback: !this.canvasPreview.allowAi,
            showResultsFeedbackAnalysis: this.canvasPreview.allowAi,
        };
        this.competitorCardDataSourceConfigs = {
            dataSourceLabel: 'Briefing.onlineBriefing.settings.competitor.source',
            showDateRange: false,
            showResultsFeedback: !this.canvasPreview.allowAi,
            showResultsFeedbackAnalysis: this.canvasPreview.allowAi,
        };
    }

    updateMonthlyConfigs(): void {
        this.monthlyConfigs = {
            monthlyDateRange: this.canvasPreview.template === OnlineBriefingTemplateEnum.ONLINE_BRIEFING_TEMPLATE_MONTHLY,
            monthlySettings: this.canvasPreview.monthlySettings,
            restrictMonthToMostRecent: this.canvasPreview?.deliverySchedulingType === deliverySchedulingTypesEnum.recurring
        } as MonthlyConfigs;
    }

    updateQuartlyConfigs() {
        this.quarterlyConfigs = {
            quarterlyDateRange: this.canvasPreview.template === OnlineBriefingTemplateEnum.ONLINE_BRIEFING_TEMPLATE_QUARTERLY,
            quarterlySettings: this.canvasPreview.quarterlySettings,
            restrictQuarterToMostRecent: this.canvasPreview.deliverySchedulingType === deliverySchedulingTypesEnum.recurring
        } as QuarterlyConfigs;
    }

    analysisFocusChanged(analysisFocus: string): void {
        this.canvasPreview.analysisFocus = analysisFocus;
        this.changes.emit();
    }

    populateCompetitors() {
        if (this.canvasPreview.template !== OnlineBriefingTemplateEnum.ONLINE_BRIEFING_TEMPLATE_COMPETITOR) {
            return;
        };
        const io: ConfirmDialogModel = {
            inputs: {
                heading: this.localizationService.transform(
                    'Briefing.onlineBriefing.settings.brand.populateCompetitors.heading',
                    { name: this.brandCard.name }
                ),
                message: this.localizationService.transform(
                    'Briefing.onlineBriefing.settings.brand.populateCompetitors.message',
                    { name: this.brandCard.name }
                ),
                cancelLabel: 'Briefing.onlineBriefing.settings.brand.populateCompetitors.cancel',
                okLabel: 'Briefing.onlineBriefing.settings.brand.populateCompetitors.confirm',
                id: 'CanvasAddCompetitors'
            },
            outputs: {
                dialogClose: (confirmed: boolean) => {
                    if (confirmed) {
                        this.monitoringBrandManagementService.getBrandCompetitors(this.brandCard)
                            .pipe(
                                takeUntil(this.destroy$)
                            ).subscribe(competitors => {
                                this.canvasPreview.competitors = competitors || [];
                                this.updateDuplicateBrandIds();
                                requestAnimationFrame(() => {
                                    this.updateHasUnsavedSource();
                                    this.competitorCards.last?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                });
                            });
                    }
                }
            }
        };
        this.coreDialogService.open(ConfirmDialogComponent, io);
    }

    addCompetitor(): void {
        this.monitoringBrandManagementService.addCompetitor(
            undefined,
            this.canvasPreview.competitors.length,
            [
                this.brandCard.color,
                ...this.canvasPreview.competitors.map(competitor => competitor.color)
            ]
        ).pipe(
            takeUntil(this.destroy$)
        ).subscribe(competitor => {
            this.canvasPreview.competitors.push(competitor);
            this.updateDuplicateBrandIds();
            requestAnimationFrame(() => {
                this.updateHasUnsavedSource();
                this.competitorCards.last?.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            });
        });
    }

    deleteCompetitor(index: number): void {
        this.canvasPreview.competitors.splice(index, 1);
        this.updateDuplicateBrandIds();
        requestAnimationFrame(() => {
            this.updateHasUnsavedSource();
        });
        this.changes.emit();
    }

    moveUp(index: number) {
        this.moveCompetitor(index, index - 1);
    }

    moveDown(index: number) {
        this.moveCompetitor(index, index + 1);
    }

    moveCompetitor(from: number, to: number) {
        const competitor = this.canvasPreview.competitors[from];
        this.canvasPreview.competitors.splice(from, 1);
        this.canvasPreview.competitors.splice(to, 0, competitor);
    }

    onBrandCardChanged() {
        this.canvasPreview.brand = this.brandCard.brand;
        this.canvasPreview.name = this.brandCard.name;
        this.canvasPreview.logo = this.brandCard.logo;
        this.canvasPreview.accentColor = this.brandCard.color;
        this.canvasPreview.dataSource = this.brandCard.dataSource;
        this.updateDuplicateBrandIds();
        this.monitoringBrandManagementService.syncCompetitorDateRangesToBrand(this.brandCard, this.canvasPreview.competitors);
        requestAnimationFrame(() => {
            this.updateHasUnsavedSource();
        });
        this.changes.emit();
    }

    onCompetitorCardChanged(competitor: MonitoringBrand, index: number) {
        this.canvasPreview.competitors[index] = competitor;
        this.updateDuplicateBrandIds();
        this.monitoringBrandManagementService.syncCompetitorDateRangesToBrand(this.brandCard, this.canvasPreview.competitors);
        requestAnimationFrame(() => {
            this.updateHasUnsavedSource();
        });
        this.changes.emit();
    }

    updateHasUnsavedSource(): void {
        const hasUnsavedSource = [
            this.canvasPreview?.dataSource,
            ...(this.canvasPreview?.competitors?.map(c => c.dataSource) ?? [])
        ].some(source => source?.isUnsavedSource());

        this.mentionSearchSettingService.updateHasUnsavedSourceType(hasUnsavedSource);
    }

    validate(): boolean {
        // Update UI: If there are errors, show error messages.
        this.allBrandCards?.toArray()?.forEach(card => card.validate());

        let isValid = true;

        isValid = isValid && !!this.canvasPreview.name?.trim() && !isNil(this.canvasPreview.dataSource?.id);

        if (this.canvasPreview.template === this.onlineBriefingTemplateEnum.ONLINE_BRIEFING_TEMPLATE_COMPETITOR) {
            isValid = this.canvasPreview.competitors?.reduce((valid, competitor) => {
                return valid && !!competitor.name?.trim() && !isNil(competitor.dataSource?.id);
            }, isValid);
            isValid = isValid && Object.keys(this.duplicateBrandIds).length === 0;
        }

        if (!isValid) {
            this.scrollErrorsIntoView();
        }
        return isValid;
    }

    scrollErrorsIntoView(): void {
        requestAnimationFrame(() => {
            const cards = this.allBrandCards?.toArray() ?? [];
            const refs = this.allBrandCardRefs?.toArray() ?? [];
            const brandCardPickerRef = this.brandCardDataSourceRef;
            const errorRefs: ElementRef[] = [];

            cards.forEach((card, i) => {
                if (card.hasNameError) {
                    errorRefs.push(refs[i]);
                }
                if (card.hasDataSourceError) {
                    errorRefs.push(!card.showDataSourceField && brandCardPickerRef ? brandCardPickerRef : refs[i]);
                }
                // cards[0] is the main brandCardTemp; competitor cards follow in the same order as canvasPreview.competitors.
                const competitorBrandId = this.canvasPreview.competitors[i - 1]?.brand?.id;
                if (competitorBrandId !== null && competitorBrandId !== undefined && this.duplicateBrandIds[competitorBrandId]) {
                    errorRefs.push(refs[i]);
                }
            });

            if (errorRefs.length > 0) {
                errorRefs[0].nativeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        });
    }
}
